import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWrapper } from '_helpers';
import { alertActions } from './alert.slice';

// Create slice

const name = 'events';
const initialState = createInitialState();
const extraActions = createExtraActions();
const reducers = createReducers();
const extraReducers = createExtraReducers();
const slice = createSlice({ name, initialState, reducers, extraReducers });

// Exports

export const eventsActions = { ...slice.actions, ...extraActions };
export const eventsReducer = slice.reducer;

// Implementation

function createInitialState() {
    return {
        data: {
            events: [],
            date_last_event: ''
        },
        initialData: {
            events: [],
            date_last_event: ''
        },
        offline: null,
        filters: [],
        isLoading: false,
        error: null,
        autoRefresh: false,
    };
}

function createReducers() {
    return {
        toggleAutoRefresh,
        addFilter,
        removeFilter,
        clearData,
    };

    function toggleAutoRefresh(state, action) {
        state.autoRefresh = action.payload;
    }

    function addFilter(state, action) {
        if (!state.filters.includes(action.payload)) {
            state.filters.push(action.payload);
        }
        if (state.initialData) {
            state.data.events = applyFilters(state.filters, state.initialData.events);
        }
    }

    function removeFilter(state, action) {
        state.filters = state.filters.filter(filter => filter !== action.payload);
        if (state.initialData) {
            state.data.events = applyFilters(state.filters, state.initialData.events);
        }
    }

    function applyFilters(filters, events) {
        if (!filters.length) return events;
        return events.filter(item => !filters.includes(item.URGENCY_LEVEL));
    }

    function clearData(state, action) {
        state.data = {
            events: [],
        }
        state.initialData = {
            events: [],
        }
    }
}

function createExtraActions() {
    const baseUrl = process.env.REACT_APP_API_URL;
    const day = new Date();
    const today = day.toISOString().split('T')[0];
    //const anyDaysAgo = new Date(day.setDate(day.getDate() - 14)).toISOString().split('T')[0];

    return {
        getAll: getAll(),
        getAlertsAndPreAlerts: getAlertsAndPreAlerts(),
        getLastEvents: getLastEvents(),
        getLastEventsByVin: getLastEventsByVin(),
        getFiltered: getFiltered(),
        getOffline: getOffline(),
    };

    /* Pour récupérer tous types d'événements */
    function getAll() {
        return createAsyncThunk(
            `${name}/getAll`,
            async function(arg, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/events?min_date=${today}&max_date=${today}`);
                    dispatch(alertActions.success({message: 'Actualisation des évènements', showAfterRedirect: true}))
                    return data;
                }catch (error) {
                    dispatch(alertActions.error({message: "Erreur lors de l'acualisation des évènements", showAfterRedirect: true}));
                }
            }
        );
    }

    /* Pour récupérer uniquement les alertes et pré-alertes */
    function getAlertsAndPreAlerts() {
        return createAsyncThunk(
            `${name}/getAlertsAndPreAlerts`,
            async function(arg, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/events?min_date=${today}&max_date=${today}&only_alerte=1&only_last=1`);
                    dispatch(alertActions.success({message: 'Actualisation des évènements', showAfterRedirect: true}))
                    return data;
                }catch (error) {
                    dispatch(alertActions.error({message: "Erreur lors de l'acualisation des évènements", showAfterRedirect: true}));
                }

            }
        );
    }

    /* Pour récupérer les 100 derniers */
    function getLastEvents() {
        return createAsyncThunk(
            `${name}/getLastEvents`,
            async function(arg, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/events?last_event=1&min_date=${today}&max_date=${today}`);
                    dispatch(alertActions.success({message: 'Actualisation des évènements', showAfterRedirect: true}))
                    return data;
                }catch (error) {
                    dispatch(alertActions.error({message: error ?? "Erreur lors de l'acualisation des évènements", showAfterRedirect: true}));
                }

            }
        );
    }

    /* Pour récupérer uniquement le dernier par véhicule */
    function getLastEventsByVin() {
        return createAsyncThunk(
            `${name}/getLastEventsByVin`,
            async function(vin, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/events?min_date=${today}&max_date=${today}&vin=${vin}`);
                    dispatch(alertActions.success({message: 'Actualisation des évènements', showAfterRedirect: true}))
                    return data;
                }catch (error) {
                    dispatch(alertActions.error({message: error ?? "Erreur lors de l'acualisation des évènements", showAfterRedirect: true}));
                }

            }
        );
    }

    /* Pour appliquer un filtre (filtreId) */
    function getFiltered() {
        return createAsyncThunk(
            `${name}/getFiltered`,
            async function(queryParams, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/events?&${queryParams}`);
                    dispatch(alertActions.success({message: 'Filtre appliqué avec succès', showAfterRedirect: true}))
                    return data;
                }catch (error) {
                    dispatch(alertActions.error({message: error ?? "Erreur lors de l'application du filtre", showAfterRedirect: true}));
                }

            }
        );
    }

    /* Pour récupérer les bus offline */
    function getOffline() {
        return createAsyncThunk(
            `${name}/getOffline`,
            async function(arg, { dispatch }) {
                try {
                    const data = await fetchWrapper.get(`${baseUrl}/offline`);
                    dispatch(alertActions.success({message: 'Actualisation des bus offline', showAfterRedirect: true}))
                    return data;
                } catch (error) {
                    dispatch(alertActions.error({message: "Erreur lors de l'acualisation bus offline", showAfterRedirect: true}));
                }
            }
        );
    }
}


function createExtraReducers() {
    return (builder) => {
        getAll();
        getAlertsAndPreAlerts();
        getLastEvents();
        getLastEventsByVin();
        getFiltered();
        getOffline();

        function handlePending(state) {
            state.pendingCount = (state.pendingCount || 0) + 1;
            state.isLoading = true;
        }

        function handleFulfilled(state, action, field = 'data', updateInitial = false) {
            state.pendingCount = (state.pendingCount || 0) - 1;
            state[field] = action.payload;
            if (updateInitial) {
                state.initialData = action.payload;
            }
            if (state.pendingCount === 0) {
                state.isLoading = false;
            }
        }

        function handleRejected(state, action) {
            state.pendingCount = (state.pendingCount || 0) - 1;
            state.error = action.error.message || 'Echec de récupération des évènements';
            if (state.pendingCount === 0) {
                state.isLoading = false;
            }
        }

        function getAll() {
            const { pending, fulfilled, rejected } = extraActions.getAll;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'data', true))
                .addCase(rejected, handleRejected);
        }

        function getAlertsAndPreAlerts() {
            const { pending, fulfilled, rejected } = extraActions.getAlertsAndPreAlerts;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'data', true))
                .addCase(rejected, handleRejected);
        }

        function getLastEvents() {
            const { pending, fulfilled, rejected } = extraActions.getLastEvents;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'data', true))
                .addCase(rejected, handleRejected);
        }

        function getLastEventsByVin() {
            const { pending, fulfilled, rejected } = extraActions.getLastEventsByVin;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'data', true))
                .addCase(rejected, handleRejected);
        }

        function getFiltered() {
            const { pending, fulfilled, rejected } = extraActions.getFiltered;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'data', true))
                .addCase(rejected, handleRejected);
        }

        function getOffline() {
            const { pending, fulfilled, rejected } = extraActions.getOffline;
            builder
                .addCase(pending, handlePending)
                .addCase(fulfilled, (state, action) => handleFulfilled(state, action, 'offline'))
                .addCase(rejected, handleRejected);
        }
    };
}



